import { styled } from '@mui/material';

import { DocumentIcon } from '../../../icons';
import { Chip } from '../../atoms/Chip';
import { Divider } from '../../atoms/Divider';
import { Tag } from '../../atoms/Tag';
import { Card } from '../Card';
import { HTMLDisplay } from '../HTMLDisplay';
import { type ContractCardProps, type ContractCardStatus, useContractCard } from './useContractCard';

type Props = ContractCardProps;

const Root = styled(Card)({
  height: '100%',
});

const FACT_MIN_WIDTH = '8.5rem';

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
}));

const HeaderText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
}));

const Title = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  margin: 0,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const Action = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(-0.5),
  marginRight: theme.spacing(-1),
}));

const Body = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingInline: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const Facts = styled('dl')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(${FACT_MIN_WIDTH}, 1fr))`,
  gap: theme.spacing(2),
  margin: 0,
}));

const Fact = styled('div')<{ ownerState?: { wide: boolean } }>(({ ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  ...(ownerState?.wide && { gridColumn: '1 / -1' }),
}));

const FactLabel = styled('dt')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const FactValue = styled('dd')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
  overflowWrap: 'anywhere',
}));

const FactHint = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  display: 'block',
  color: theme.palette.text.secondary,
}));

const PriceValue = styled(FactValue)(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  whiteSpace: 'pre-line',
}));

const Section = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const SectionLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

/**
 * A provider contract: term dates, renewal, notice period, pricing, key information
 * and the signed document. The status `Tag` flags terms that need attention.
 */
export function ContractCard(props: Props) {
  const hook = useContractCard(props);

  return (
    <Root data-testid={props['data-testid'] ?? 'contract-card'}>
      <Header>
        <HeaderText>
          <Title>{props.title}</Title>
          {props.status ? (
            <Tag color={props.status.color} label={props.status.label} variantIcon="state" />
          ) : null}
        </HeaderText>
        {props.action ? <Action>{props.action}</Action> : null}
      </Header>

      <Body>
        <Facts>
          <Fact>
            <FactLabel>Souscription</FactLabel>
            <FactValue>{hook.formattedStartDate}</FactValue>
          </Fact>
          <Fact>
            <FactLabel>Échéance</FactLabel>
            <FactValue>
              {hook.formattedEndDate}
              {props.renewalLabel ? <FactHint>{props.renewalLabel}</FactHint> : null}
            </FactValue>
          </Fact>
          {hook.hasNoticePeriod ? (
            <Fact>
              <FactLabel>Préavis</FactLabel>
              <FactValue>{props.noticePeriod}</FactValue>
            </Fact>
          ) : null}
          {hook.hasPrice ? (
            <Fact ownerState={{ wide: true }}>
              <FactLabel>Prix</FactLabel>
              <PriceValue>{props.price}</PriceValue>
            </Fact>
          ) : null}
        </Facts>

        {hook.hasDescription ? (
          <>
            <Divider />
            <Section>
              <SectionLabel>Informations clefs</SectionLabel>
              <HTMLDisplay content={props.description!} />
            </Section>
          </>
        ) : null}

        {props.documentUrl ? (
          <div>
            <Chip
              icon={<DocumentIcon fontSize="small" />}
              label={hook.documentLabel}
              href={props.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
            />
          </div>
        ) : null}
      </Body>
    </Root>
  );
}

export type { ContractCardProps, ContractCardStatus };
